import { FormActionsMui } from '@app/components/form/actions/FormActionsMui';
import { Page } from '@app/components/page/Page';
import { ComposeIcon } from '@app/modules/compose/components/ComposeIcon';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import { PublishConnectionSelector } from '@app/modules/social-export/components/PublishConnectionSelector';
import { useMediaLimitsIndication } from '@app/modules/social-export/hooks/useMediaLimitsIndication';
import { compositionToSocialExportPublishSource } from '@app/modules/social-export/lib/social-export-publish-source';
import {
  MediaLimitsAlert,
  MediaSelector,
  NoSourcesAlert,
  VideoPreview,
  publishMessages,
} from '@app/modules/social-export/publish';
import { webApi } from '@mi-front/api-clients';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

import { useComposeSocialExportUploadPage } from './useComposeSocialExportUploadPage';

const publishLabelMessage = defineMessage({
  id: 'socialExport.form.publish',
  defaultMessage: 'Publicar',
});

export interface ComposeSocialExportUploadPageContentProps {
  composition: webApi.EnrichedCompositionDetailDto;
  edits: webApi.EnrichedVideoEditorEditListItemDto[];
}

/**
 * Layout for composition social upload once composition and edits are loaded.
 */
export function ComposeSocialExportUploadPageContent({
  composition,
  edits,
}: ComposeSocialExportUploadPageContentProps) {
  const { $t } = useIntl();
  const {
    platform,
    selectedSource,
    setSelectedSource,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedRenderedEdit,
    renderedVideoUrl,
    PublishForm,
    handleUpload,
    connections,
    selectedConnectionId,
    setSelectedConnectionId,
    areConnectionsResolved,
    settingsQuery,
    stationId,
  } = useComposeSocialExportUploadPage({ composition, edits });

  const effectiveMediaUrl = renderedVideoUrl;
  const canPublish = effectiveMediaUrl !== null && hasSelectedRenderedEdit;

  const title = $t(publishMessages.publishToPlatform, { platformName: platform.name });
  const publishSource = compositionToSocialExportPublishSource(composition);

  const selectedConnection = useMemo(
    () => connections.find((c) => c.id === selectedConnectionId),
    [connections, selectedConnectionId],
  );
  // Composition edit duration is not on list DTOs; soft display tips still apply via station tier.
  const mediaLimitsIndication = useMediaLimitsIndication(selectedConnection, undefined);

  // Keep the connection selector mounted while settings refetch so changing
  // account does not replace the whole page with a spinner.
  const areSettingsReady = !settingsQuery.isLoading && settingsQuery.data !== undefined;

  const pageShell = (children: ReactNode) => (
    <Page htmlTitle={title}>
      <Page.Header
        icon={platform.Icon()}
        title={title}
        breadcrumbs={
          <Page.Breadcrumbs
            items={[
              {
                icon: <ComposeIcon fontSize="inherit" />,
                content: composition.name,
                href: composeRouteGenerator.view(composition.id),
              },
            ]}
          />
        }
      />
      <Page.Content maxWidth="lg">{children}</Page.Content>
    </Page>
  );

  if (!stationId) {
    return pageShell(<Box sx={{ mt: 2 }}>{$t(publishMessages.noStation)}</Box>);
  }

  if (!areConnectionsResolved) {
    return pageShell(
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>,
    );
  }

  if (connections.length === 0) {
    return pageShell(
      <Typography color="text.secondary">
        <FormattedMessage
          id="socialExport.connections.noneAssignedForStation"
          defaultMessage="No hay cuentas conectadas asignadas a esta emisora para esta plataforma."
        />
      </Typography>,
    );
  }

  if (!selectedConnectionId) {
    return pageShell(
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>,
    );
  }

  return (
    <SocialExportContext.Provider value={{ mediaUrl: effectiveMediaUrl ?? '' }}>
      {pageShell(
        !hasAvailableSources ? (
          <NoSourcesAlert
            description={publishMessages.panoramaNoSourcesComposition}
            linkTo={composeRouteGenerator.edits(composition.id)}
          />
        ) : (
          <>
            {showMediaSelector && (
              <Box sx={{ mb: 4, mt: 2 }}>
                <MediaSelector
                  includeFragmentOption={false}
                  value={selectedSource}
                  onChange={setSelectedSource}
                  edits={edits}
                />
              </Box>
            )}
            {!canPublish && selectedSource
              ? $t(publishMessages.mediaSelectorRenderNotReadyComposition)
              : effectiveMediaUrl && (
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={7}>
                      <PublishConnectionSelector
                        connections={connections}
                        selectedConnectionId={selectedConnectionId}
                        onSelectedConnectionChange={setSelectedConnectionId}
                        platform={platform}
                      />
                      {mediaLimitsIndication ? (
                        <MediaLimitsAlert indication={mediaLimitsIndication} />
                      ) : null}
                      {areSettingsReady ? (
                        <PublishForm
                          key={selectedConnectionId}
                          source={publishSource}
                          videoUrl={effectiveMediaUrl}
                          onUpload={handleUpload}
                          defaultSettings={settingsQuery.data}
                          connectionId={selectedConnectionId}
                          actions={
                            <FormActionsMui
                              separator
                              saveIcon={<UploadIcon />}
                              saveLabel={$t(publishLabelMessage)}
                              disableSave={mediaLimitsIndication?.blocksPublish}
                              submitDisabledTooltip={
                                mediaLimitsIndication?.blocksPublish
                                  ? mediaLimitsIndication.message
                                  : undefined
                              }
                              cancel
                            />
                          }
                        />
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                          <CircularProgress />
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <VideoPreview videoUrl={effectiveMediaUrl} />
                    </Grid>
                  </Grid>
                )}
          </>
        ),
      )}
    </SocialExportContext.Provider>
  );
}
